import React, { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from "react";

declare global {
  interface Window {
    YT?: {
      Player: new (
        elementId: string,
        options: {
          videoId: string;
          width?: number | string;
          height?: number | string;
          playerVars?: Record<string, number | string>;
          events?: {
            onReady?: (event: { target: YTPlayer }) => void;
            onStateChange?: (event: { data: number; target: YTPlayer }) => void;
          };
        }
      ) => YTPlayer;
      PlayerState: { PLAYING: number; PAUSED: number; ENDED: number; BUFFERING: number };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

export interface YTPlayer {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  setVolume: (vol: number) => void;
  mute: () => void;
  unMute: () => void;
  isMuted: () => boolean;
  destroy: () => void;
}

export interface SongYouTubePlayerRef {
  play: (offset?: number) => void;
  pause: () => void;
  seekTo: (seconds: number) => void;
  /** Reproduce sin cambiar posición (útil tras seekTo). */
  resumeVideo: () => void;
  setVolume: (vol: number) => void;
  getCurrentTime: () => number;
  isReady: () => boolean;
}

function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

interface SongYouTubePlayerProps {
  url: string;
  onReady?: () => void;
  onStateChange?: (isPlaying: boolean) => void;
  className?: string;
}

export const SongYouTubePlayer = forwardRef<SongYouTubePlayerRef, SongYouTubePlayerProps>(
  ({ url, onReady, onStateChange, className }, ref) => {
    const videoId = extractYouTubeId(url);
    const playerRef = useRef<YTPlayer | null>(null);
    const [ready, setReady] = useState(false);
    const uniqueId = useRef(`yt-player-${Math.random().toString(36).slice(2, 9)}`);

    const initPlayer = useCallback(() => {
      if (!videoId || !window.YT) return;
      const el = document.getElementById(uniqueId.current);
      if (!el) return;
      playerRef.current = new window.YT.Player(uniqueId.current, {
        videoId,
        width: "100%",
        height: "100%",
        playerVars: {
          autoplay: 0,
          controls: 1,
          modestbranding: 1,
          rel: 0,
          enablejsapi: 1,
          origin: window.location.origin,
        },
        events: {
          onReady: (e) => {
            setReady(true);
            e.target.setVolume(50);
            onReady?.();
          },
          onStateChange: (e) => {
            const ps = window.YT!.PlayerState;
            if (e.data === ps.BUFFERING) return;
            const playing = e.data === ps.PLAYING;
            onStateChange?.(playing);
          },
        },
      });
    }, [videoId, onReady, onStateChange]);

    useEffect(() => {
      if (!videoId) return;
      if (window.YT && window.YT.Player) {
        initPlayer();
        return;
      }
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScript = document.getElementsByTagName("script")[0];
      firstScript.parentNode?.insertBefore(tag, firstScript);
      window.onYouTubeIframeAPIReady = () => {
        initPlayer();
      };
      return () => {
        playerRef.current?.destroy();
        playerRef.current = null;
      };
    }, [videoId, initPlayer]);

    useImperativeHandle(ref, () => ({
      play: (offset?: number) => {
        if (!playerRef.current) return;
        if (typeof offset === "number" && offset > 0) {
          playerRef.current.seekTo(offset, true);
        }
        playerRef.current.playVideo();
      },
      pause: () => {
        playerRef.current?.pauseVideo();
      },
      seekTo: (seconds: number) => {
        playerRef.current?.seekTo(seconds, true);
      },
      resumeVideo: () => {
        playerRef.current?.playVideo();
      },
      setVolume: (vol: number) => {
        playerRef.current?.setVolume(vol);
      },
      getCurrentTime: () => playerRef.current?.getCurrentTime() ?? 0,
      isReady: () => ready,
    }));

    if (!videoId) {
      return (
        <div
          className={`rounded-lg bg-[#F5F0E8] border border-[#E8E0D4] flex items-center justify-center text-[#8A8078] text-xs ${className || ""}`}
          style={{ aspectRatio: "16/9" }}
        >
          Ingresa una URL de YouTube
        </div>
      );
    }

    return (
      <div className={`rounded-lg overflow-hidden bg-black border border-[#E8E0D4] ${className || ""}`}>
        <div id={uniqueId.current} style={{ width: "100%", aspectRatio: "16/9" }} />
      </div>
    );
  }
);

SongYouTubePlayer.displayName = "SongYouTubePlayer";
